import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supportStatus } from "../lib/holidays";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, FileText, LifeBuoy, MessagesSquare } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { Pictogram } from "../components/brand/Pictogram";
import { ColorBand } from "../components/brand/ColorBand";
import { CaseCard } from "../components/CaseCard";
import { CtaBand } from "../components/CtaBand";
import { UseCaseCard } from "../components/UseCaseCard";
import { THEMES, KPIS, BRANDS, CASES, IMG, SOLUTIONS, USE_CASES, COMMITMENT, CONTACT } from "../data/content";

const DOORS = [
  ["/devis", "Je veux un devis", "Un formulaire guidé en 3 étapes, deux minutes suffisent.", FileText, "#45ADE0", "home-door-quote"],
  ["/support", "J'ai besoin d'assistance", "Hotline, demande urgente, prise en main à distance.", LifeBuoy, "#E3B321", "home-door-support"],
  ["/contact", "Parler à un expert", "Un périmètre à confier, un support à structurer : échangeons.", MessagesSquare, "#A6C46B", "home-door-expert"],
];

const WORDS = [
  ["…", "#ffffff", 1, "heroOffice"],
  ["maîtrisée", "#EE8445", 1, "heroTeam"],
  ["documentée", "#A6C46B", 1, "heroTeam"],
  ["pilotée", "#E3B321", 1, "heroGraphs"],
  ["supervisée", "#45ADE0", 1, "heroGraphs"],
  ["sécurisée", "#A6C46B", 1, "heroRack"],
  ["simple", "#45ADE0", 1, "heroRack"],
  ["disponible", "#EE8445", 1, "heroOffice"],
  ["…", "#ffffff", 1, "heroOffice"],
];

const useWordIndex = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setTimeout(() => setI((n) => (n + 1) % WORDS.length), 2200 * WORDS[i][2]);
    return () => clearTimeout(id);
  }, [i]);
  useEffect(() => {
    [...new Set(WORDS.map((x) => x[3]))].forEach((k) => { new Image().src = IMG[k]; });
  }, []);
  return i;
};

const RotatingWord = ({ i }) => {
  const [w, c] = WORDS[i];
  return (
    <span className="inline-block relative" style={{ clipPath: "inset(-0.05em -0.2em -0.2em -0.2em)" }} data-testid="hero-rotating-word">
      <AnimatePresence mode="wait">
        <motion.span key={w} className="inline-block" style={{ color: c }} initial={{ y: "100%", opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: "-100%", opacity: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}>
          {w === "…" ? w : `${w}.`}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

const HeroPhoto = ({ i }) => {
  const key = WORDS[i][3];
  return (
    <div className="relative aspect-[4/3] lg:aspect-auto lg:h-full lg:min-h-[380px] w-full overflow-hidden bg-ink" data-testid="hero-photo">
      <AnimatePresence initial={false}>
        <motion.img key={key} src={IMG[key]} alt="Centre de services ENOVA" data-testid={`hero-photo-${key}`} className="absolute inset-0 h-full w-full object-cover" initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.9, ease: "easeOut" }} />
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
    </div>
  );
};

const SupportStatus = () => {
  const [open, label] = supportStatus();
  const dot = open ? "#A6C46B" : "#EE8445";
  return (
    <a href={CONTACT.hotlineHref} data-testid="hero-support-status" className="absolute left-4 right-4 bottom-4 sm:right-auto flex items-center gap-4 pl-4 pr-5 py-3 bg-ink/55 backdrop-blur-xl border border-white/15 text-white transition-colors hover:bg-ink/75">
      <span className="relative flex h-2.5 w-2.5 shrink-0">
        {open && <span className="absolute inline-flex h-full w-full rounded-full animate-ping opacity-60" style={{ background: dot }} />}
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full" style={{ background: dot }} />
      </span>
      <span className="leading-tight">
        <span className="block text-sm font-semibold" data-testid="hero-support-status-label">{label}</span>
        <span className="block text-xs text-white/65 mt-0.5">Lun – Ven, 7h – 17h · {CONTACT.hotline}</span>
      </span>
    </a>
  );
};

const Hero = () => {
  const i = useWordIndex();
  return (
  <section data-testid="home-hero" className="relative bg-ink text-white pt-28 lg:pt-36 overflow-hidden grain">
    <div className="relative mx-auto max-w-7xl px-5 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-6 items-stretch pb-16 lg:pb-24">
      <Reveal className="lg:col-span-8 self-center">
        <Eyebrow color="#45ADE0" light>Prestataire IT B2B · Nouvelle-Calédonie</Eyebrow>
        <div className="mt-7 lg:max-w-[700px] xl:max-w-[780px]" data-testid="hero-headline">
          <h1 data-testid="hero-title" className="font-display text-2xl sm:text-3xl xl:text-4xl font-extrabold leading-[1.1] lg:whitespace-nowrap">
            Votre informatique est{" "}
            <RotatingWord i={i} />
          </h1>
          <div className="mt-4 lg:mt-5 flex items-center gap-6">
            <span className="hidden lg:block h-px flex-1 bg-gradient-to-r from-transparent via-white/15 to-white/40" />
            <p data-testid="hero-signature" className="font-display text-2xl sm:text-3xl xl:text-4xl font-extrabold text-white lg:text-right">Notre responsabilité.</p>
          </div>
        </div>
        <p data-testid="hero-scope" className="mt-8 text-base md:text-lg text-white/85 font-medium">
          Infrastructure, support utilisateurs, serveurs, cloud, réseaux, téléphonie et automatisation.
        </p>
        <p data-testid="hero-subtitle" className="mt-3 text-sm md:text-base text-white/55 max-w-xl leading-relaxed">
          ENOVA accompagne les entreprises dans l'exploitation, le support et l'évolution de leur système d'information.
        </p>
        <div className="mt-9 flex flex-wrap gap-3">
          <Link to="/services" data-testid="hero-services-btn" className="btn-primary">Découvrir nos services <ArrowRight size={18} /></Link>
          <Link to="/contact" data-testid="hero-expert-btn" className="btn-ghost-light"><MessagesSquare size={16} /> Parler à un expert</Link>
        </div>
      </Reveal>
      <Reveal delay={0.15} className="lg:col-span-4 relative">
        <HeroPhoto i={i} />
        <SupportStatus />
      </Reveal>
    </div>
    <ColorBand height={8} />
  </section>
  );
};

const Doors = () => (
  <section className="mx-auto max-w-7xl px-5 lg:px-8 -mt-px pt-16 lg:pt-20">
    <div className="grid md:grid-cols-3 gap-4">
      {DOORS.map(([to, title, desc, Icon, color, id], i) => (
        <Reveal key={to} delay={i * 0.08}>
          <Link to={to} data-testid={id} className="door-card group" style={{ "--c": color }}>
            <Icon size={26} style={{ color }} />
            <h3 className="font-display text-xl font-bold mt-6">{title}</h3>
            <p className="text-sm text-ink/60 mt-2">{desc}</p>
            <ArrowRight className="absolute right-6 top-6 group-hover:translate-x-1 transition-transform" size={20} />
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);

const Commitment = () => (
  <section className="mx-auto max-w-7xl px-5 lg:px-8 pt-24 lg:pt-32" data-testid="home-commitment">
    <div className="grid lg:grid-cols-12 gap-10">
      <Reveal className="lg:col-span-5">
        <Eyebrow color="#45ADE0">Responsabilité opérationnelle</Eyebrow>
        <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">Confiez-nous tout ou partie de votre SI.</h2>
        <p className="mt-5 text-ink/65 text-base md:text-lg leading-relaxed">La gestion complète de votre informatique, un périmètre précis, une activité de support ou une équipe : vous choisissez ce que vous nous confiez. Nous en assurons la continuité de service.</p>
      </Reveal>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
        {COMMITMENT.map(([title, d], i) => (
          <Reveal key={title} delay={i * 0.07} className="bg-paper p-7">
            <span className="font-display text-sm font-bold text-[#45ADE0]">0{i + 1}</span>
            <h3 className="mt-3 font-display text-xl font-bold">{title}</h3>
            <p className="mt-2 text-sm text-ink/60">{d}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Themes = () => (
  <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32">
    <div className="grid lg:grid-cols-12 gap-8 mb-14">
      <Reveal className="lg:col-span-5">
        <Eyebrow>Nos métiers</Eyebrow>
        <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">Du support utilisateurs<br />aux infrastructures.</h2>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7 self-end">
        <p className="text-ink/65 text-base md:text-lg leading-relaxed">Chaque métier peut être confié seul ou combiné aux autres. Derrière, c'est la même équipe qui exploite votre environnement et en connaît chaque élément.</p>
      </Reveal>
    </div>
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {THEMES.map((t, i) => (
        <Reveal key={t.slug} delay={i * 0.05} className={i < 2 ? "lg:col-span-2" : ""}>
          <Link to={`/services/${t.slug}`} data-testid={`home-theme-${t.slug}`} className="theme-card group" style={{ "--c": t.color }}>
            <Pictogram name={t.icon} color={t.color} size={i < 2 ? 64 : 52} />
            <h3 className="font-display text-2xl font-bold mt-8">{t.name}</h3>
            <p className="text-sm text-white/60 mt-2 max-w-sm">{t.short}</p>
            <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold" style={{ color: t.color }}>Découvrir <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></span>
          </Link>
        </Reveal>
      ))}
      <Reveal delay={0.3} className="sm:col-span-2">
        <div className="grid sm:grid-cols-2 gap-3 h-full" data-testid="home-solutions">
          {SOLUTIONS.map((s) => (
            <Link key={s.slug} to={`/solutions/${s.slug}`} data-testid={`home-solution-${s.slug}`} className="border border-ink/15 p-4 hover:bg-white transition-colors flex justify-between items-center gap-3">
              <span><span className="text-[11px] uppercase tracking-[0.18em] text-ink/50">Solution</span><span className="block font-display text-base font-bold leading-snug">{s.title}</span></span>
              <ArrowRight size={18} />
            </Link>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

const Kpis = () => (
  <section className="bg-white border-y border-ink/10" data-testid="home-kpis">
    <div className="mx-auto max-w-7xl px-5 lg:px-8 pt-20 pb-16">
      <Reveal>
        <Eyebrow>ENOVA en chiffres</Eyebrow>
        <h2 className="mt-5 font-display text-3xl sm:text-4xl font-extrabold">Une capacité opérationnelle, mesurée.</h2>
      </Reveal>
      <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
        {KPIS.map((k, i) => (
          <Reveal key={k.label} delay={(i % 4) * 0.06} className={`bg-white p-5 lg:p-8 relative ${i === KPIS.length - 1 && KPIS.length % 2 ? "col-span-2" : ""}`}>
            {k.placeholder && <span className="mb-3 lg:mb-0 inline-block lg:absolute lg:top-3 lg:right-3 text-[10px] uppercase tracking-wider px-2 py-0.5 border border-dashed border-ink/30 text-ink/45">À renseigner</span>}
            <p data-testid={`kpi-value-${i}`} className={`font-display font-extrabold whitespace-nowrap ${k.value.length > 7 ? "text-xl sm:text-2xl lg:text-4xl" : "text-3xl lg:text-5xl"} ${k.placeholder ? "text-ink/30" : ""}`}>{k.value}</p>
            <p className="text-sm text-ink/60 mt-2 max-w-[14rem]">{k.label}</p>

          </Reveal>
        ))}
      </div>
    </div>
    <div className="border-t border-ink/10 overflow-hidden py-6">
      <div className="marquee flex items-center gap-20 whitespace-nowrap" data-testid="home-brands">
        {[...BRANDS, ...BRANDS].map(([name, file, h = "h-8"], i) => (
          <img key={i} src={`/brands/${file}`} alt={name} data-testid={`brand-logo-${i}`} className={`${h} w-auto max-w-[150px] object-contain shrink-0 grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-[filter,opacity]`} />
        ))}
      </div>
    </div>
  </section>
);

const FEATURED = ["minier-service-desk", "hotel-du-centre"].map((id) => CASES.find((c) => c.id === id));

const Cases = () => (
  <section className="bg-ink text-white py-24 lg:py-32 grain relative" data-testid="home-cases">
    <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
      <div className="flex flex-wrap justify-between items-end gap-6 mb-12">
        <Reveal className="max-w-2xl">
          <Eyebrow color="#EE8445" light>Réalisations</Eyebrow>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">Des projets structurés, exploités dans la durée.</h2>
        </Reveal>
        <Link to="/realisations/references" data-testid="home-all-cases-link" className="btn-ghost-light">Toutes les réalisations <ArrowRight size={16} /></Link>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-4" data-testid="home-use-cases">
        {USE_CASES.map((u, i) => (
          <Reveal key={u.id} delay={i * 0.06} className={i < 2 ? "lg:col-span-3" : "lg:col-span-2"}>
            <UseCaseCard u={u} large={i < 2} />
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const References = () => (
  <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32" data-testid="home-references">
    <Reveal>
      <Eyebrow color="#EE8445">Références</Eyebrow>
      <h2 className="mt-5 font-display text-3xl sm:text-4xl font-extrabold">Ils nous ont confié leur SI.</h2>
    </Reveal>
    <div className="mt-12 grid md:grid-cols-2 gap-5">
      {FEATURED.map((c, i) => <Reveal key={c.id} delay={i * 0.1}><CaseCard c={c} featured /></Reveal>)}
    </div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Doors />
      <Commitment />
      <Themes />
      <Kpis />
      <Cases />
      <References />
      <CtaBand title="Parlons du périmètre que vous souhaitez nous confier." />
    </div>
  );
}
